import type { IProcessAction } from 'common/interfaces/IProcessAction';
import { Box, Card, Link, Text } from '@ksyos/design-system';
import { useSelector } from 'react-redux';
import type { IReduxState } from '../../../configureStore';
import { fetchJson } from '../../../store/fetch';
import { ExternalLink } from '../../elements/atoms/externalLink/ExternalLink';
import { Container } from '../../elements/molecules/container/Container';
import { PageSection } from '../../elements/molecules/pageSection/PageSection';

export function OsasOptInFlowWaitingForCall({
    osasStartFlowAction,
    setPatientClickedContinue,
}: Readonly<{
    osasStartFlowAction: IProcessAction;
    setPatientClickedContinue: (value: boolean) => void;
}>) {
    const patientHomePhoneNumber = useSelector((state: IReduxState) => state.authentication.user?.homePhoneNumber);
    const patientMobileNumber = useSelector((state: IReduxState) => state.authentication.user?.mobileNumber);

    return (
        <Container isFluidOnMobile>
            <PageSection>
                <Card>
                    <Card.Header title="We bellen je zo snel mogelijk" />
                    <Card.Body>
                        <Box maxWidth={620}>
                            <Text
                                mb={4}
                                size="md"
                            >
                                {phoneNumberText()}
                                <Link
                                    color="secondary"
                                    fontWeight="normal"
                                    href="tel:020 600 00 60"
                                    openInNewTab
                                    variant="externalLink"
                                >
                                    020 600 00 60
                                </Link>
                                .
                            </Text>
                            <Text
                                fontWeight="bold"
                                size="md"
                            >
                                Je kunt altijd nog starten met jouw slaapzorg
                            </Text>
                            <Text size="md">
                                Om meer te lezen over de vergoeding, bekijk{' '}
                                <ExternalLink href="https://www.ksyos.nl/patienten/slaap#component_heading:~:text=Wat%20kost%20een%20slaaponderzoek%3F">
                                    Wat kost een slaaponderzoek?
                                </ExternalLink>{' '}
                                Mocht je toch willen starten, klik dan{' '}
                                <Link
                                    color="secondary"
                                    fontWeight="normal"
                                    onClick={async () => {
                                        await fetchJson(
                                            `/api/patient/processes/${osasStartFlowAction.processId}`,
                                            'POST',
                                            {
                                                actionId: osasStartFlowAction.id,
                                                serviceWorkflowStepId: osasStartFlowAction.serviceWorkflowStep.id,
                                            }
                                        );
                                        setPatientClickedContinue(true);
                                    }}
                                    variant="button"
                                >
                                    Start mijn slaapzorg
                                </Link>
                                .
                            </Text>
                            <Text
                                mb={4}
                                size="md"
                            >
                                <strong>Let op:</strong> kies je ervoor om te starten met je slaapzorg, dan word je niet
                                meer gebeld.
                            </Text>
                            <Text
                                mb={2}
                                size="md"
                            >
                                Sluit dit tabblad als je wacht om gebeld te worden.
                            </Text>
                        </Box>
                    </Card.Body>
                </Card>
            </PageSection>
        </Container>
    );

    function phoneNumberText() {
        if (patientHomePhoneNumber || patientMobileNumber) {
            const phoneNumber = [patientHomePhoneNumber, patientMobileNumber].filter(Boolean).join(' of ');

            return `We bellen je binnen 2 werkdagen op ${phoneNumber}. Controleer je telefoonnummer. Klopt het niet? Bel ons dan op `;
        }

        return `Er is geen telefoonnummer bij ons bekend. Bel ons op `;
    }
}
